import { listFeatures, readLegacyLabels, submitActionTrackingFrom } from "../tracking/FeatureTracking";
import { eventQBus } from "../types/EventQBus";
import { move, moveAllChildren } from "../util/Utils";
import { filterFooter, filterForm } from "../filtersheet/Selectors";
import type { TrackingLabels } from "../tracking/TrackingLabels";
import { filterFeatureRootSelectors, updateFeatureStatus } from "./FilterTracking";
import { filterHeaderButtons } from "../filtersheet/FilterSheetHeaderButtons";
import { readSanPopularFacetValues, updateSanPopularFacetValues } from "./SveltePopularFacetValues";
import { HeurekaElementFactory } from "../util/HeurekaElementFactory";
import { Filter } from "./Filter";
import { isMobile } from "../util/Breakpoint";
import { mergeLabel } from "../tracking/GlobalTracking";
import type { ActionName } from "../tracking/Tracking";
import { toggleFilterAccordion } from "./SvelteComponentInitialization";
import { OcFilterAccordions } from "./OcFilterAccordions";
import { isActive } from "../toggle/Toggle";

const FILTER_ACCORDION_CLASS = "heureka_filterAccordion";
const FILTER_ACCORDION_OPEN_ON_MOBILE_CLASS = `${FILTER_ACCORDION_CLASS}--openOnMobile`;

const ACCORDION_CONTENT_SELECTOR = ".heureka_filterAccordion__content";
const NEXT_BEST_ACTION_BUTTONS_SELECTOR = ".find_filter__nextBestFilterButtons";

export class OcFilterAccordion {
  static readonly factory = HeurekaElementFactory.byClass(FILTER_ACCORDION_CLASS, OcFilterAccordion);

  constructor(readonly accordion: HTMLElement) {}

  /*                  */

  static filterId(id: string, root?: ParentNode | null) {
    return OcFilterAccordion.factory.pick(`.${FILTER_ACCORDION_CLASS}[data-filter-id="${id}"]`, root);
  }

  static invalidFilterIds() {
    return OcFilterAccordion.factory
      .all()
      .filter((accordion) => accordion.invalid)
      .map((accordion) => accordion.filterId);
  }

  /*               */

  static register() {
    eventQBus.on("heureka.filterSection.loaded", OcFilterAccordion.addToggleListeners);
    eventQBus.on("heureka.filterSection.loaded", OcFilterAccordion.initAllFeatureStatus);
    eventQBus.once("heureka.filterSection.loaded", OcFilterAccordion.initAllFoldoutStatus);
    eventQBus.once("heureka.filterSection.loaded", OcFilterAccordion.initAll);
  }

  private static initAllFeatureStatus() {
    if (!isMobile()) {
      OcFilterAccordions.template().accordions.forEach((acc) => {
        if (acc.open) {
          /*                                                          */
          acc.filter?.updateChildrenFeatureStatus(true);
        }
      });
    }
    if (isMobile()) {
      updateFeatureStatus("visible", "loaded", filterFeatureRootSelectors());
    }
  }

  private static initAllFoldoutStatus(accordion: OcFilterAccordion) {
    if (isMobile()) {
      accordion.openMobile = false;
    }
  }

  private static initAll() {
    if (isMobile()) {
      updateFeatureStatus("visible", "loaded", filterFeatureRootSelectors());
      OcFilterAccordion.factory.forEach(OcFilterAccordion.initAllFoldoutStatus);
    }
  }

  private static addToggleListeners() {
    OcFilterAccordion.factory.forEach(OcFilterAccordion.addToggleListener);
  }

  static prepareAll(fragment: DocumentFragment) {
    if (isMobile()) {
      OcFilterAccordion.factory.forEach(OcFilterAccordion.prepareMobile, fragment);
    } else {
      OcFilterAccordion.factory.forEach((accordion) => OcFilterAccordion.prepare(accordion), fragment);
    }
  }

  private static prepareMobile(accordion: OcFilterAccordion) {
    const oldAccordion = OcFilterAccordion.filterId(accordion.filterId);
    if (oldAccordion) {
      accordion.openMobile = oldAccordion.openMobile;
    }
  }

  static prepare(accordion: OcFilterAccordion) {
    const oldAccordion = OcFilterAccordion.filterId(accordion.filterId);
    if (oldAccordion) {
      accordion.open = oldAccordion.open;
    }
  }

  /*                       */

  get filterId(): string {
    return this.accordion.dataset.filterId || "";
  }

  isExpandable(): boolean {
    return this.accordion.matches("oc-accordion-v1");
  }

  get expanded() {
    return this.accordion.hasAttribute("expanded");
  }

  set expanded(expand: boolean) {
    this.accordion.toggleAttribute("expanded", expand);
  }

  get hidden(): boolean {
    return this.accordion.hidden;
  }

  get content() {
    return this.accordion.querySelector<HTMLElement>(ACCORDION_CONTENT_SELECTOR) || null;
  }

  get filterHeaderButtons(): HTMLElement | null {
    return filterHeaderButtons(this.accordion);
  }

  set filterHeaderButtons(source) {
    moveAllChildren(source, this.filterHeaderButtons, true);
  }

  get filterFooter(): HTMLElement | null {
    return filterFooter(this.accordion);
  }

  set filterFooter(source) {
    moveAllChildren(source, this.filterFooter, true);
  }

  get nextBestActionButtons(): HTMLElement | null {
    return this.accordion.querySelector(NEXT_BEST_ACTION_BUTTONS_SELECTOR);
  }

  set nextBestActionButtons(source) {
    moveAllChildren(source, this.nextBestActionButtons, true);
  }

  get filterForm(): HTMLFormElement | null {
    return filterForm(this.accordion);
  }

  set filterForm(form) {
    move(form, this.content);
  }

  /**
 *
 */
  get filter(): Filter | undefined {
    return Filter.filterId(this.filterId);
  }

  /**
 *
 */
  get nestedFilter(): Filter | undefined {
    return Filter.factory.pick(undefined, this.accordion);
  }

  set nestedFilter(filter) {
    if (filter) {
      move(filter.form, this.content);
    }
  }

  get invalid() {
    return !!this.accordion.querySelector(".find_filterMessage[data-message-text]");
  }

  get alwaysOpenOnMobile() {
    return this.accordion.classList.contains(FILTER_ACCORDION_OPEN_ON_MOBILE_CLASS);
  }

  get openMobile(): boolean {
    return this.alwaysOpenOnMobile || !this.isExpandable();
  }

  set openMobile(state: boolean) {
    /*                                                                                                                                             */
    /*                                                                               */
    const oldState = this.forceHeaderState(state);
    if (oldState != state || this.alwaysOpenOnMobile) {
      this.filterFoldoutStatus = oldState === undefined || this.alwaysOpenOnMobile ? "initial_opened" : state;
    }
  }

  get open(): boolean {
    /*                                                    */
    return this.accordion.hasAttribute("expanded");
  }

  set open(state: boolean) {
    const oldState = this.forceHeaderState(state);
    if (oldState != state) {
      this.filterFoldoutStatus = oldState === undefined ? "initial_opened" : state;
    }
  }

  set filterFoldoutStatus(value: boolean | TrackingLabels["san_FilterFoldoutStatus"]) {
    const filter = isActive("HEUREKA_2398_OPTIMIZE_FILTER_SHEET_PERFORMANCE") ? this.filter : this.nestedFilter;
    if (filter) {
      if (typeof value !== "boolean") {
        filter.filterFoldoutStatus = value;
      } else {
        filter.filterFoldoutStatus = value ? "opened" : "closed";
      }
    }
  }

  set sanPopularFacetValues(value: TrackingLabels["san_PopularFacetValues"]) {
    updateSanPopularFacetValues(this.accordion, value);
  }

  mergeLabel<T extends keyof TrackingLabels>(label: T, value: TrackingLabels[T]) {
    mergeLabel(this.accordion, label, value);
  }

  private forceHeaderState(state: boolean) {
    if (this.isExpandable()) {
      const oldState = this.expanded;
      this.expanded = state;
      return oldState;
    }
    /*                                     */
  }

  private trackAccordionToggle(open: boolean) {
    const { filterId } = this;
    this.filterFoldoutStatus = open;
    toggleFilterAccordion(filterId, open);
    let trackingData: Partial<TrackingLabels> = {
      san_FilterFoldoutType: filterId,
      san_FilterFoldoutActivity: open ? "open" : "close",
    };

    const hasPopularFacetValues: boolean | undefined = this.filter?.hasVisiblePopularValues;

    if (hasPopularFacetValues) {
      trackingData = { ...trackingData, ...{ san_PopularFacetValues: readSanPopularFacetValues() } };
    }

    const labels = readLegacyLabels(this.accordion);
    if (labels) {
      trackingData = { ...trackingData, ...labels };
    }
    this.filter?.updateChildrenFeatureStatus(open);
    const name: ActionName = open ? "open" : "close";
    submitActionTrackingFrom(this.accordion, name, trackingData, listFeatures(["Filter-Overview"]));
  }

  /*                 */

  private static addToggleListener(accordion: OcFilterAccordion) {
    accordion.accordion.addEventListener("oc-accordion-open", () => accordion.trackAccordionToggle(true));
    accordion.accordion.addEventListener("oc-accordion-close", () => accordion.trackAccordionToggle(false));
  }

  remove() {
    const parentElement = this.accordion?.parentElement;
    if (parentElement?.matches("oc-row-v2")) {
      parentElement.remove();
    }
    this.accordion?.remove();
  }
}
